import { isValidElement, useState, type MouseEvent, type ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * The one way this app explains a badge, a control or a column: a shadcn
 * tooltip, never a native `title`. Native titles can't be styled, can't hold
 * structure, and open on an OS delay that reads as lag next to the Radix ones.
 *
 * A plain-text child is wrapped in a hoverable span so "there's an explanation
 * here" looks the same everywhere; an element child becomes the trigger itself
 * and keeps its own styling. Passing no content renders the child alone, so a
 * call site with an optional note doesn't have to branch.
 *
 * Touch has no hover, so on a coarse pointer a tap toggles the hint — unless
 * the trigger sits in something tappable (a link, a button, a checkbox), where
 * the tap belongs to that control. `tap` claims the tap for a control whose
 * only job is to show the hint, such as an info icon.
 */
const INTERACTIVE = 'a,button,input,select,textarea,label,[role=button],[role=checkbox],[role=link],[role=tab]';

export function Hint({
  content,
  children,
  side = 'top',
  align,
  className,
  contentClassName,
  underline = true,
  delayDuration = 300,
  tap = false,
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Applied to the generated span — only when children is plain text. */
  className?: string;
  contentClassName?: string;
  /** The dotted rule marking hintable text. Off for bare numbers and icons. */
  underline?: boolean;
  delayDuration?: number;
  /** Tapping the trigger toggles the hint even though it is a control. */
  tap?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (content == null || content === '' || content === false) {
    return <>{children}</>;
  }
  return (
    <Tooltip delayDuration={delayDuration} open={open} onOpenChange={setOpen}>
      <TooltipTrigger
        asChild
        onClick={(e: MouseEvent<HTMLElement>) => {
          if (!window.matchMedia('(pointer: coarse)').matches) return;
          if (!tap && e.currentTarget.closest(INTERACTIVE)) return;
          // Radix closes a tooltip on trigger click; this tap opens it instead.
          e.preventDefault();
          setOpen((o) => !o);
        }}
      >
        {isValidElement(children) ? children : (
          <span
            className={cn(
              'cursor-help',
              underline && 'underline decoration-dotted decoration-current/40 underline-offset-2',
              className
            )}
          >
            {children}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        className={cn('max-w-[20rem] text-left leading-relaxed', contentClassName)}
      >
        {content}
      </TooltipContent>
    </Tooltip>
  );
}
